import React, { useContext } from 'react'

import { DeleteOutlined, DownOutlined, EditOutlined, EyeOutlined } from '@ant-design/icons';
import { Button, Dropdown, Space, Switch, Table, Tag, Typography } from 'antd'

import createOperation from '../marketing/operations/MarketingOperationCreate';
import { apiDeleteOperation } from '../../helpers/Api';
import { Context } from '../../helpers/Context';
import { addKeys } from '../../helpers/AddKey';
import confirmModal from '../DiConfirmModal';

import dayjs from 'dayjs';
import { daysOfWeek, monthsOfYear } from '../../helpers/DateUtils';

export default function TableMarketingOperations({ operationsData, loading, setLoading, onChange, onSave }) {
   

   const context = useContext(Context);
   const { publicCode, successNotification, errorNotification  } = context;
   
    /////////////////////////////////////////////////////////////////////////////////////////// 
            //////////////////// azioni di un'operazione ///////////////
    ///////////////////////////////////////////////////////////////////////////////////////////

      const deleteOperation = async (operationId) => {
            
         setLoading(true);

         if (!publicCode || !operationId) {
            errorNotification("Codice pubblico o ID operazione assente");
            return;
         }

         try {
            const confirm = await confirmModal(
               "Sei sicuro di voler eliminare questa operazione? L'azione è irreversibile",
               context
            );

            if (!confirm) {
               setLoading(false)
               return;
            }

            const res = await apiDeleteOperation(publicCode, operationId);

            if (res && !res.message) {
               successNotification("Operazione eliminata con successo!");
               onChange?.(); // usa opzionale nel caso onChange non esista
               setLoading(false);
            } else {
               errorNotification(res?.message || "Errore sconosciuto durante l'eliminazione");
               setLoading(false);
            }
         } catch (error) {
            console.error("Errore durante l'eliminazione:", error);
            errorNotification(error?.message || "Errore imprevisto");
            setLoading(false);
         }
      };

   
    /////////////////////////////////////////////////////////////////////////////////////////// 
            //////////////////// azioni di un'operazione ///////////////
    ///////////////////////////////////////////////////////////////////////////////////////////

   
   
   
   // Columns for Marketing Operations
   const columns = [
      {
         title: 'Titolo',
         dataIndex: 'title',
         key: 'title',
      },
     {
         title: 'Canali',
         dataIndex: 'operation_channels',
         key: 'operation_channels',
         render: (channels) => {
            return channels.map((channelObj, index) => {
               const key = Object.keys(channelObj)[0]; // es. "PUSH", "EMAIL", ecc.
               
               let color;
               switch (key) {
               case 'EMAIL':
                  color = 'cyan';
                  break;
               case 'SMS':
                  color = 'purple';
                  break;
               case 'WHATSAPP':
                  color = 'green';
                  break;
               case 'PUSH':
                  color = 'yellow';
                  break;
               case 'COUPON':
                  color = 'orange';
                  break;
               default:
                  color = 'gray';
               }

               return <Tag color={color} key={`${key}-${index}`}>{key}</Tag>;
            });
         }
         },

      {
         title: 'Pubblico',
         dataIndex: 'audienceCampaigns',
         key: 'audienceCampaigns',
         render: (audiences) => {
            if (!Array.isArray(audiences) || audiences.length === 0) {
               return  <Tag color='red'>Nessun'appartenenza</Tag>;
            }
   
            if (audiences.length === 1) {
               return <Tag>{audiences[0].title}</Tag>;
            }
   
            return (
               <Dropdown
                  overlayClassName="ant-dropdown-link"
                  menu={{
                  items: audiences.map((audience) => ({
                     label: audience.title,
                     key: audience.id,
                  })),
                  }}
                  trigger={['click']}
               >
                  <a onClick={(e) => e.preventDefault()}>
                  <Space>
                     Visualizza pubblico
                     <DownOutlined />
                  </Space>
                  </a>
               </Dropdown>
            );
         }
      },

      {
         title: 'Programmazione',
         dataIndex: 'schedule',
         key: 'schedule',
         render: (schedule) => {
            if (!schedule || !Array.isArray(schedule) || schedule.length === 0) return 'Nessuna programmazione';

            const item = schedule[0]; // supponiamo un solo blocco per semplicità
            const { schedule_type, days_of_week, full_date, execution_times, monthly_day_number, monthly_week_number, monthly_day_of_week, yearly_day, yearly_month } = item;

            const getTime = () => execution_times?.[0] ? dayjs(execution_times[0]).format('HH:mm') : '';
            const getRange = () =>
               full_date?.length === 2
               ? `dal ${dayjs(full_date[0]).format('DD/MM/YYYY')} al ${dayjs(full_date[1]).format('DD/MM/YYYY')}`
               : '';

            switch (schedule_type) {
               case 'immediate':
               return 'Invio immediato';

               case 'daily':
               return `Ogni giorno alle ${getTime()} ${getRange()}`;

               case 'weekly':
               const days = (days_of_week || []).map((d) => daysOfWeek[d]).join(', ');
               return `Ogni settimana il ${days} alle ${getTime()} ${getRange()}`;

               case 'monthly_specific_day':
               return `Ogni mese il giorno ${monthly_day_number} alle ${getTime()} ${getRange()}`;

               case 'monthly_specific_day_week':
               const weekNames = ['Prima', 'Seconda', 'Terza', 'Quarta', 'Quinta'];
               return `Ogni mese la ${weekNames[monthly_week_number - 1]} ${daysOfWeek[monthly_day_of_week]} alle ${getTime()} ${getRange()}`;

               case 'yearly':
               return `Ogni anno il ${yearly_day} ${monthsOfYear[yearly_month - 1]} alle ${getTime()} ${getRange()}`;

               default:
               return 'Tipo sconosciuto';
            }
         }
      },
      {
         title: 'Azioni',
         key: 'actions',
         render: (_, record) => (
            <Space size="middle">
               {
               <Button
                  onClick={()=>createOperation({context:context ,operationId:record.id, onSave:onSave})}
                  icon={<EditOutlined />}
                  className='color-primary'
                  type="link"
               />
               /* 
               <Button
                  icon={<EyeOutlined />}
                  className='color-yellow'
                  type="link"
               /> 
               */}
               <Button
                  onClick={()=>deleteOperation(record.id)}
                  icon={<DeleteOutlined />}
                  type="link"
                  danger
               />
            </Space>
         ),
      },

   ];

  return (
   <Table
      dataSource={addKeys(operationsData)}
      columns={columns}
      loading={loading}
      bordered
      scroll={{ x: 'max-content' }} 
   />
  )
}
